import {
  ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { differenceInDays, isPast, parse } from 'date-fns';
import { of } from 'rxjs';
import { filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

import { Maybe } from 'graphql/jsutils/Maybe';

import {
  CompleteActiveFollowUpsGQL, EntityCoreFragment, EntityWithBasicInfoFragment,
  EntityWithFollowUpDetailsFragment, FollowUpDetailsFragment, GetEntityById2GQL, NoteTarget, UserPublic
} from 'generated/graphql';

import { selectCurrentUser } from 'app/_state/auth/auth-selectors';
import { NoteEditDialog } from 'app/note/dialogs/note-edit-dialog/note-edit-dialog.component';
import { NotePermissionsService } from 'app/note/note-permissions.service';
import { WithChangesSubject } from 'core/mixins/with-changes-subject';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { ToastrService } from 'core/toastr.service';
import { ConfirmDialogComponent } from 'shared/components/confirm-dialog/confirm-dialog-component';
import { FollowUpFlagDueDateDialog } from 'entity/dialogs/follow-up-flag-due-date/follow-up-flag-due-date.dialog';
import { ReassignFollowUpFlagDialog } from 'entity/dialogs/reassign-follow-up-flag/reassign-follow-up-flag.dialog';
import { FeatureFlagService } from 'feature-flags/feature-flag.service';

import type { Observable } from 'rxjs';
import { FullNamePipe } from 'shared/pipes/full-name.pipe';
@Component({
  selector: 'app-entity-flag-table',
  templateUrl: './entity-flag-table.component.html'
})
export class EntityFlagTableComponent extends WithDisposableSubject(
  WithChangesSubject(class { }))
  implements OnChanges, OnInit {
  isSaving = false;

  showFullText = false;
  canEditNote = false;

  entity: EntityCoreFragment & EntityWithBasicInfoFragment & Maybe<EntityWithFollowUpDetailsFragment>;

  currentUserId: string;
  assignedUser: Maybe<UserPublic>;

  @Output() followUpCompleted = new EventEmitter<EntityWithBasicInfoFragment | EntityCoreFragment | EntityWithFollowUpDetailsFragment>();

  @Input() entityId: string;
  @Input() campaignId: string;

  get activeFollowUp() {
    return this.entity?.activeFollowUp;
  }

  get dueDateEndOfDay() {
    return this.activeFollowUp?.dueDate + ' 23:59:59';
  }

  get tableHighlightClass() {
    const parsedDueDate = parse(this.dueDateEndOfDay, 'yyyy-MM-dd HH:mm:ss', 0);
    const diff = differenceInDays(new Date(), parsedDueDate);

    // if due date is today or in the past, show red table
    if (isPast(parsedDueDate) || diff === 0) {
      return 'table--highlight table--highlight-alert';
    // if due date is within 2 weeks, show orange table
    } else if (diff >= -14) {
      return 'table--highlight';
    // otherwise, show normal (gray) table
    } else {
      return '';
    }
  }

  get dueDateTextClass() {
    const parsedDueDate = parse(this.dueDateEndOfDay, 'yyyy-MM-dd HH:mm:ss', 0);
    const diff = differenceInDays(new Date(), parsedDueDate);

    // if due date is today or in the past, show red table
    if (isPast(parsedDueDate) || diff === 0) {
      return 'table--highlight-text';
    } else {
      return 'color--gray';
    }
  }

  constructor(
    private _getEntityById: GetEntityById2GQL,
    private _completeFollowUpsGQL: CompleteActiveFollowUpsGQL,
    private _toastrService: ToastrService,
    private _cd: ChangeDetectorRef,
    private _notePermissionsService: NotePermissionsService,
    private _store: Store,
    private _dialog: MatDialog,
    private _featureService: FeatureFlagService,
  ) {
    super();
  }

  ngOnInit() {
    this._inputChanges$().pipe(
      switchMap((changes) => this._buildVM$(changes))
    ).subscribe(
      ({ entity, canEditNote }) => {
        this.entity = entity;
        this.canEditNote = canEditNote;
        this.assignedUser = entity?.activeFollowUp?.assignedUser;
        this._cd.markForCheck();
      }
    );

    this._store.select(
      selectCurrentUser
    ).pipe(
      takeUntil(this.disposer)
    ).subscribe((cu) => {
      this.currentUserId = cu.id;
      this._cd.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
  }

  reassignFollowUp(followUp: FollowUpDetailsFragment) {
      ReassignFollowUpFlagDialog.open(this._dialog, {
        data: {
          entityName: new FullNamePipe().transform(this.entity),
          campaignId: this.campaignId,
          followUp: followUp || this.activeFollowUp
        }
      });
  }

  editNote(followUp: FollowUpDetailsFragment) {
    NoteEditDialog.open(this._dialog, {
      data: {
        targetType: NoteTarget.FollowUp,
        targetId: followUp.note.id,
        note: followUp.note
      }
    });
  }

  editDueDate(followUp: FollowUpDetailsFragment) {
    FollowUpFlagDueDateDialog.open(this._dialog, {
      data: {
        entityName: new FullNamePipe().transform(this.entity),
        campaignId: this.campaignId,
        followUp: followUp || this.activeFollowUp
      }
    });
  }

  completeFollowUpFlag() {
    ConfirmDialogComponent.open(this._dialog, {
      data: {
        message: `Marking this follow up as complete will remove the flag.`
      }
    })
      .afterClosed()
      .pipe(
        switchMap(proceed => {
          if (!proceed) {
            return of(this.entity);
          }
          return this._completeFollowUpsGQL.mutate({
            input: {
              entityId: this.entity.id,
              campaignId: this.entity.campaignContextId
            }
          }).pipe(
            map(({ data }) => data.completeActiveFollowUps.entity),
            tap((entity) => {
              this.followUpCompleted.emit(entity);
              this._toastrService.success('Follow Up Flag Completed');
            })
          );
        })
      )
      .subscribe((entity) => {
        this.entity = entity;
      });
  }

  private _buildVM$(input: { entityId: string; campaignId: string }) {
    return this._getEntityById.watch({
      entityId: input.entityId,
      campaignId: input.campaignId,
      withToplines: false,
      withFollowUpDetails: true
    }).valueChanges.pipe(
      takeUntil(this.disposer),
      switchMap(({ data }) => {
        const entity = data.getEntity;
        const followUp = entity?.activeFollowUp;
        const note = followUp?.note;

        if (note) {
          return this._notePermissionsService.canEditNote(
            note, followUp.campaignId, this.campaignId
          ).pipe(
            map(canEditNote => ({ entity, canEditNote }))
          );
        }

        return of({ entity, canEditNote: false });
      })
    );
  }

  private _inputChanges$(): Observable<{ campaignId: string; entityId: string }> {
    return this.watchForChangeKeys('entityId', 'campaignId').pipe(
      startWith<any, any>(null),
      takeUntil(this.disposer),
      map(() => ({ campaignId: this.campaignId, entityId: this.entityId })),
      filter(({ campaignId, entityId }) => !!campaignId || !!entityId)
    );
  }
}
